import { Subject } from 'rxjs';

export interface SceneLoadProgressEvent {
  /** Current stage name (e.g. 'textures', 'models') */
  stage: string;
  /** 0-based index of the current stage */
  stageIndex: number;
  /** Total number of stages */
  stageCount: number;
  /** 0..1 progress within the current stage */
  stageProgress: number;
  /** 0..1 weighted overall progress */
  overallProgress: number;
  /** Optional detail, e.g. asset filename */
  detail?: string;
}

interface LoadStage {
  name: string;
  weight: number;
}

const DEFAULT_STAGES: LoadStage[] = [
  { name: 'manifest', weight: 1 },
  { name: 'textures', weight: 3 },
  { name: 'models', weight: 5 },
  { name: 'objects', weight: 1 },
  { name: 'lights', weight: 0.5 },
  { name: 'skybox', weight: 2 },
  { name: 'navigation', weight: 3 },
  { name: 'effects', weight: 1 },
  { name: 'features', weight: 1 },
  { name: 'physics', weight: 0.5 },
  { name: 'finalize', weight: 0.5 },
];

export class SceneLoadProgressTracker {
  public readonly progress$ = new Subject<SceneLoadProgressEvent>();

  private stages: LoadStage[];
  private totalWeight: number;
  private currentStageIndex = -1;
  private completedWeight = 0;

  constructor(stages?: LoadStage[]) {
    this.stages = stages ?? DEFAULT_STAGES;
    this.totalWeight = this.stages.reduce((sum, s) => sum + s.weight, 0);
  }

  beginStage(name: string): void {
    // Complete the previous stage's weight
    if (this.currentStageIndex >= 0) {
      this.completedWeight += this.stages[this.currentStageIndex].weight;
    }

    this.currentStageIndex = this.stages.findIndex((s) => s.name === name);
    if (this.currentStageIndex === -1) {
      // Unknown stage — append dynamically so progress still works
      this.stages.push({ name, weight: 1 });
      this.totalWeight += 1;
      this.currentStageIndex = this.stages.length - 1;
    }

    this.emit(0);
  }

  updateStageProgress(fraction: number, detail?: string): void {
    this.emit(Math.min(1, Math.max(0, fraction)), detail);
  }

  complete(): void {
    this.completedWeight = this.totalWeight;
    this.emit(1);
    this.progress$.complete();
  }

  private emit(stageProgress: number, detail?: string): void {
    const stage = this.stages[this.currentStageIndex];
    const overallProgress = Math.min(
      1,
      (this.completedWeight + stage.weight * stageProgress) / this.totalWeight,
    );

    this.progress$.next({
      stage: stage.name,
      stageIndex: this.currentStageIndex,
      stageCount: this.stages.length,
      stageProgress,
      overallProgress,
      detail,
    });
  }
}
